//@flow
import React from "react"
import { connect } from "react-redux"
import { push } from "react-router-redux"
import { Motion, StaggeredMotion, spring } from "react-motion"
import { Route, Switch, Redirect } from "react-router-dom"
import { TweenMax, Cubic } from "gsap"

import { isModal } from "../../Modal"
import SVGLoader from "../../UI/SVGLoader"
import CurrentAirQualityView from "../CurrentAirQualityView"
import CommitView from "../CommitView"
import BodyView from "../BodyView"
import WoodburningView from "../WoodburningView"
import TellMeMoreView from "../TellMeMoreView"
import { extendSession, sessionLengthMature } from "../redux"

import NavigationBar from "./NavigationBar"
import NavigationItems from "./NavigationItems"
import NavigationItem from "./NavigationItem"
import NavigationTab from "./NavigationTab"
import NavigationTabIcon from "./NavigationTabIcon"
import NavigationTabBackground from "./NavigationTabBackground"
import NavigationOverlayFill from "./NavigationOverlayFill"
import Container from "./Container"

import iconHome from "../Icons/iconHome.svg"
import iconMenu from "../Icons/iconMenu.svg"
import iconDaily from "../Icons/iconDaily.svg"
import iconTalk from "../Icons/iconTalk.svg"
import iconForecast from "../Icons/iconForecast.svg"
import iconLungs from "../Icons/iconLungs.svg"
import iconFire from "../Icons/iconFire.svg"
import iconSurvey from "../Icons/iconSurvey.svg"

import type { Style } from "react-motion"
import type { Location } from "react-router-dom"

const springConfig = { stiffness: 300, damping: 50 }

const hardSpringConfig = { stiffness: 300, damping: 26 }

type State = {
  active: boolean
}

type Props = {
  location: Location,
  isModal: boolean,
  extendSession: Function,
  sessionLengthMature: boolean,
  push: any => any
}

type TabInfo = {
  iconPath: string,
  messageKey: string,
  url: string,
  modal?: boolean,
  active: () => boolean
}

class Navigator extends React.Component<Props, State> {
  container: ?HTMLElement
  blurElement: { a: number }

  constructor(props: Props) {
    super(props)
    this.state = { active: false }
    this.blurElement = { a: 0 }
    this.container = null
  }

  handleItemClick = (pathname: string, modal: ?boolean) => {
    console.log({ pathname, state: { modal } })
    this.props.push({ pathname, state: { modal } })
    this.setState(() => ({ active: false }))
  }

  handleTabClick = () => {
    const { extendSession } = this.props
    extendSession()
    this.setState(() => ({ active: !this.state.active }))
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { isModal } = this.props
    if (prevProps.isModal !== isModal) {
      TweenMax.to(this.blurElement, 0.5, {
        a: isModal ? 5 : 0,
        ease: Cubic.easeInOut,
        onUpdate: () => {
          TweenMax.set(this.container, {
            webkitFilter: "blur(" + this.blurElement.a + "px)",
            filter: "blur(" + this.blurElement.a + "px)"
          })
        }
      })
    }
  }

  render() {
    const { sessionLengthMature } = this.props
    const { active } = this.state

    const style: Style = active
      ? {
          y: spring(0, springConfig),
          opacity: spring(0.35, springConfig)
        }
      : {
          y: spring(-90, springConfig),
          opacity: spring(0.0, springConfig)
        }

    const tabs: Array<TabInfo> = [
      {
        iconPath: iconHome,
        messageKey: "kiosk.navigation.home",
        url: "/kiosk/home/default",
        active: () => true
      },
      {
        iconPath: iconForecast,
        messageKey: "kiosk.navigation.current",
        url: "/kiosk/section/current",
        active: () => true
      },
      {
        iconPath: iconFire,
        messageKey: "kiosk.navigation.woodburning",
        url: "/kiosk/section/woodburning",
        active: () => true
      },
      {
        iconPath: iconLungs,
        messageKey: "kiosk.navigation.body",
        url: "/kiosk/section/body",
        active: () => true
      },
      {
        iconPath: iconDaily,
        messageKey: "kiosk.navigation.commit",
        url: "/kiosk/section/commit",
        active: () => true
      },
      {
        iconPath: iconTalk,
        messageKey: "kiosk.navigation.more",
        url: "/kiosk/section/more",
        active: () => true
      },
      {
        iconPath: iconSurvey,
        messageKey: "kiosk.navigation.survey",
        url: "/kiosk/survey",
        modal: true,
        active: () => sessionLengthMature
      }
    ]

    return (
      <Container innerRef={el => (this.container = el)}>
        <Motion style={style}>
          {({ opacity }) => (
            <NavigationOverlayFill
              style={{ opacity: opacity }}
              active={active}
            />
          )}
        </Motion>
        <Motion style={style}>
          {({ y }) => (
            <NavigationBar
              style={{
                transform: `translate3d(0, ${y}px, 0)`
              }}
            >
              <NavigationTab onClick={this.handleTabClick}>
                <NavigationTabIcon>
                  <SVGLoader
                    path={active ? iconMenu : iconHome}
                    fill="#fff"
                    height="49px"
                    width="49px"
                  />
                </NavigationTabIcon>
                <NavigationTabBackground />
              </NavigationTab>
              <StaggeredMotion
                defaultStyles={[
                  { y: -25, opacity: 0 },
                  { y: -25, opacity: 0 },
                  { y: -25, opacity: 0 },
                  { y: -25, opacity: 0 },
                  { y: -25, opacity: 0 },
                  { y: -25, opacity: 0 },
                  { y: -25, opacity: 0 }
                ]}
                styles={prevInterpolatedStyles =>
                  !prevInterpolatedStyles
                    ? []
                    : prevInterpolatedStyles.map((_, i) => {
                        const prevStyle = prevInterpolatedStyles
                          ? prevInterpolatedStyles[i - 1]
                          : {}
                        return i === 0
                          ? {
                              opacity: spring(active ? 1 : 0, springConfig),
                              y: spring(active ? 0 : -25, hardSpringConfig)
                            }
                          : {
                              opacity: spring(prevStyle.opacity, springConfig),
                              y: spring(prevStyle.y, hardSpringConfig)
                            }
                      })}
              >
                {interpolatingStyles => (
                  <NavigationItems>
                    {interpolatingStyles.map((style, i) => (
                      <NavigationItem
                        path={tabs[i].iconPath}
                        key={i}
                        opacity={style.opacity}
                        y={style.y}
                        messageKey={tabs[i].messageKey}
                        onClick={() =>
                          this.handleItemClick(tabs[i].url, tabs[i].modal)}
                        active={tabs[i].active()}
                      />
                    ))}
                  </NavigationItems>
                )}
              </StaggeredMotion>
            </NavigationBar>
          )}
        </Motion>
        <Switch>
          <Route
            path="/kiosk/section/current"
            component={CurrentAirQualityView}
          />
          <Route
            path="/kiosk/section/woodburning"
            component={WoodburningView}
          />
          <Route path="/kiosk/section/commit" component={CommitView} />
          <Route path="/kiosk/section/more" component={TellMeMoreView} />
          <Route path="/kiosk/section/body" component={BodyView} />
          <Route render={() => <Redirect to="/kiosk/home" />} />
        </Switch>
      </Container>
    )
  }
}

export default connect(
  state => ({
    isModal: isModal(state),
    sessionLengthMature: sessionLengthMature(state)
  }),
  {
    push,
    extendSession
  }
)(Navigator)



// WEBPACK FOOTER //
// ./src/Kiosk/Navigator/index.js